import { useState } from 'react'
import { clsx } from 'clsx'
import Button from './Button'
import Input from './Input'
import styles from './CustomReminderModal.module.css'
import type { RepeatConfig, Reminder } from '@/services/api'

interface CustomReminderModalProps {
  reminder?: Reminder | null  // If provided, we're editing an existing reminder
  onSave: (scheduledAt: Date, repeatConfig: RepeatConfig) => void
  onClose: () => void
  className?: string
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
]

const getDefaultEndDate = (): string => {
  const date = new Date()
  date.setMonth(date.getMonth() + 1)
  return date.toISOString().split('T')[0]
}

const getMinDate = (): string => {
  return new Date().toISOString().split('T')[0]
}

const getDefaultTime = (): string => {
  const now = new Date()
  now.setHours(now.getHours() + 1)
  now.setMinutes(0)
  return `${String(now.getHours()).padStart(2, '0')}:00`
}

// Helper to extract date and time from ISO string
const getDateFromISO = (isoString: string): string => {
  return new Date(isoString).toISOString().split('T')[0]
}

const getTimeFromISO = (isoString: string): string => {
  const date = new Date(isoString)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

export default function CustomReminderModal({
  reminder,
  onSave,
  onClose,
  className,
}: CustomReminderModalProps) {
  // Initialize state from reminder if editing, otherwise use defaults
  const [date, setDate] = useState(() =>
    reminder ? getDateFromISO(reminder.scheduled_at) : getMinDate()
  )
  const [time, setTime] = useState(() =>
    reminder ? getTimeFromISO(reminder.scheduled_at) : getDefaultTime()
  )
  const [isRepeat, setIsRepeat] = useState(() =>
    reminder?.repeat_config?.type !== 'once' && reminder?.repeat_config?.type !== undefined
  )
  const [repeatType, setRepeatType] = useState<'daily' | 'weekly' | 'custom'>(() => {
    if (reminder?.repeat_config?.type && reminder.repeat_config.type !== 'once') {
      return reminder.repeat_config.type
    }
    return 'daily'
  })
  const [customDays, setCustomDays] = useState<number[]>(() =>
    reminder?.repeat_config?.days || []
  )
  const [endDate, setEndDate] = useState(() =>
    reminder?.repeat_config?.end_date || getDefaultEndDate()
  )
  const [isLoading, setIsLoading] = useState(false)

  const isEditing = !!reminder

  const toggleDay = (day: number) => {
    if (customDays.includes(day)) {
      setCustomDays(customDays.filter((d) => d !== day))
    } else {
      setCustomDays([...customDays, day].sort())
    }
  }

  const handleSave = async () => {
    setIsLoading(true)

    try {
      // Combine date and time
      const [hours, minutes] = time.split(':').map(Number)
      const scheduledAt = new Date(date)
      scheduledAt.setHours(hours, minutes, 0, 0)

      // Validate scheduled time is in the future
      if (scheduledAt <= new Date()) {
        alert('Please select a time in the future')
        setIsLoading(false)
        return
      }

      // Build repeat config
      const repeatConfig: RepeatConfig = isRepeat
        ? {
            type: repeatType,
            ...(repeatType === 'custom' && { days: customDays }),
            end_date: endDate,
          }
        : { type: 'once' }

      await onSave(scheduledAt, repeatConfig)
    } finally {
      setIsLoading(false)
    }
  }

  const isValid = date && time && (!isRepeat || repeatType !== 'custom' || customDays.length > 0)

  return (
    <div className={clsx(styles.modal, className)}>
      <div className={styles.header}>
        <h3 className={styles.title}>{isEditing ? 'Edit Reminder' : 'Custom Reminder'}</h3>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className={styles.content}>
        {/* Date and Time */}
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>Date</label>
            <Input
              type="date"
              value={date}
              min={getMinDate()}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Time</label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        </div>

        {/* Repeat Toggle */}
        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Repeat</span>
          <button
            className={clsx(styles.toggle, isRepeat && styles.toggleOn)}
            onClick={() => setIsRepeat(!isRepeat)}
            role="switch"
            aria-checked={isRepeat}
          >
            <span className={styles.toggleThumb} />
          </button>
        </div>

        {/* Repeat Options */}
        {isRepeat && (
          <div className={styles.repeatOptions}>
            <div className={styles.repeatTypeRow}>
              {(['daily', 'weekly', 'custom'] as const).map((type) => (
                <button
                  key={type}
                  className={clsx(
                    styles.repeatTypeButton,
                    repeatType === type && styles.repeatTypeActive
                  )}
                  onClick={() => setRepeatType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* Custom Days */}
            {repeatType === 'custom' && (
              <div className={styles.daysRow}>
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    className={clsx(
                      styles.dayButton,
                      customDays.includes(day.value) && styles.dayActive
                    )}
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            )}

            {/* End Date */}
            <div className={styles.field}>
              <label className={styles.label}>End date</label>
              <Input
                type="date"
                value={endDate}
                min={date}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button variant="ghost" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {isEditing ? 'Update Reminder' : 'Save Reminder'}
        </Button>
      </div>
    </div>
  )
}
